import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { PERIODICITIES } from "./schedules";
import type { ContactQuery, Gender, ListState, PerPage, Periodicity, Sort, VisitStatusFilter } from "./api";

const SORTS: Sort[] = ["newest", "name_asc", "name_desc", "recently_edited", "next_visit"];
const GENDERS: Gender[] = ["male", "female", "male_only", "female_only"];
const VISIT_STATUSES: VisitStatusFilter[] = ["never_visited", "postponed"];
const PER_PAGES: PerPage[] = [25, 50, 100];
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const MAX_PAGE = 100000;
const isRealDay = (v: string): boolean => {
  if (!DAY.test(v)) return false;
  const d = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
};
// Today's app links here with its own keys (notifications: address_needs_attention=1; TrialWidget: currentPage,
// perPage). They are read when the new key is absent and dropped on the next write. `import=1` and today's
// `open=import` belong to the list page (they open the import dialog, R19) and are left alone here.
const TODAYS_KEYS = ["address_needs_attention", "currentPage", "perPage"];
// The keys this module owns; anything else in the URL is left alone.
const OWNED = ["q", "attention", "possible_duplicate", "visit", "due_by", "not_visited_since", "schedule", "cat", "cat_x", "cat_none", "proj", "proj_x", "zip", "gender", "sort", "page", "per_page", ...TODAYS_KEYS];

const nonEmpty = <T>(a?: T[]): a is T[] => !!a && a.length > 0;

/* ---------- wire (Laravel) ---------- */

/** Snake-case keys, booleans as 1/0, empty values left out. */
export function toWire(q: ContactQuery): Record<string, unknown> {
  const w: Record<string, unknown> = {};
  if (q.q) w.q = q.q;
  if (q.needsAttention) w.needs_attention = 1;
  if (q.possibleDuplicate) w.possible_duplicate = 1;
  if (nonEmpty(q.visitStatus)) w.visit_status = q.visitStatus;
  if (q.dueBy) w.due_by = q.dueBy;
  if (q.notVisitedSince) w.not_visited_since = q.notVisitedSince;
  if (nonEmpty(q.schedule)) w.schedule = q.schedule;
  if (q.categories) {
    const c: Record<string, unknown> = {};
    if (nonEmpty(q.categories.include)) c.include = q.categories.include;
    if (nonEmpty(q.categories.exclude)) c.exclude = q.categories.exclude;
    // The server refuses "none" together with an include list: include wins.
    if (q.categories.none && !nonEmpty(q.categories.include)) c.none = 1;
    if (Object.keys(c).length) w.categories = c;
  }
  if (q.projects) {
    const p: Record<string, unknown> = {};
    if (nonEmpty(q.projects.include)) p.include = q.projects.include;
    if (nonEmpty(q.projects.exclude)) p.exclude = q.projects.exclude;
    if (Object.keys(p).length) w.projects = p;
  }
  if (nonEmpty(q.zip)) w.zip = q.zip;
  if (q.gender) w.gender = q.gender;
  if (q.sort) w.sort = q.sort;
  return w;
}

/** One entry per non-empty filter group (search and sort are not filters). */
export function activeFilterCount(q: ContactQuery): number {
  return [
    !!q.needsAttention,
    !!q.possibleDuplicate,
    nonEmpty(q.visitStatus) || !!q.dueBy || !!q.notVisitedSince,
    nonEmpty(q.schedule),
    nonEmpty(q.categories?.include) || nonEmpty(q.categories?.exclude) || !!q.categories?.none,
    nonEmpty(q.zip),
    !!q.gender,
    nonEmpty(q.projects?.include) || nonEmpty(q.projects?.exclude),
  ].filter(Boolean).length;
}

/* ---------- URL ---------- */

export function toSearchParams(s: ListState): URLSearchParams {
  const p = new URLSearchParams();
  const set = (k: string, v: string | undefined) => v && p.set(k, v);
  const list = (k: string, v?: (string | number)[]) => nonEmpty(v) && p.set(k, v.join(","));
  set("q", s.q);
  if (s.needsAttention) p.set("attention", "1");
  if (s.possibleDuplicate) p.set("possible_duplicate", "1");
  list("visit", s.visitStatus);
  set("due_by", s.dueBy);
  set("not_visited_since", s.notVisitedSince);
  list("schedule", s.schedule);
  list("cat", s.categories?.include);
  list("cat_x", s.categories?.exclude);
  if (s.categories?.none) p.set("cat_none", "1");
  list("proj", s.projects?.include);
  list("proj_x", s.projects?.exclude);
  s.zip?.forEach((z) => z && p.append("zip", z));
  set("gender", s.gender);
  set("sort", s.sort);
  if (s.page > 1) p.set("page", String(s.page));
  if (s.perPage !== 25) p.set("per_page", String(s.perPage));
  return p;
}

const csv = (p: URLSearchParams, k: string) =>
  (p.get(k) ?? "").split(",").map((x) => x.trim()).filter(Boolean);
const ids = (p: URLSearchParams, k: string) =>
  csv(p, k).filter((x) => /^\d+$/.test(x)).map(Number).filter((n) => n > 0);
const oneOf = <T extends string>(allowed: T[], v: string | null): T | undefined =>
  allowed.includes(v as T) ? (v as T) : undefined;

/** Unknown or invalid values are dropped; page is at least 1 and per_page one of 25/50/100. */
export function fromSearchParams(p: URLSearchParams): ListState {
  const s: ListState = { page: 1, perPage: 25 };
  const page = Number(p.get("page") ?? p.get("currentPage"));
  if (Number.isInteger(page) && page >= 1 && page <= MAX_PAGE) s.page = page;
  const per = Number(p.get("per_page") ?? p.get("perPage"));
  if (PER_PAGES.includes(per as PerPage)) s.perPage = per as PerPage;

  const q = p.get("q");
  if (q) s.q = q;
  if ((p.get("attention") ?? p.get("address_needs_attention")) === "1") s.needsAttention = true;
  if (p.get("possible_duplicate") === "1") s.possibleDuplicate = true;
  const visit = csv(p, "visit").filter((x): x is VisitStatusFilter => VISIT_STATUSES.includes(x as VisitStatusFilter));
  if (visit.length) s.visitStatus = visit;
  const dueBy = p.get("due_by");
  if (dueBy && isRealDay(dueBy)) s.dueBy = dueBy;
  const since = p.get("not_visited_since");
  if (since && isRealDay(since)) s.notVisitedSince = since;
  const schedule = csv(p, "schedule").filter((x): x is Periodicity => PERIODICITIES.includes(x as Periodicity));
  if (schedule.length) s.schedule = schedule;

  const categories: NonNullable<ContactQuery["categories"]> = {};
  const catInclude = ids(p, "cat");
  const catExclude = ids(p, "cat_x");
  if (catInclude.length) categories.include = catInclude;
  if (catExclude.length) categories.exclude = catExclude;
  if (p.get("cat_none") === "1") categories.none = true;
  if (Object.keys(categories).length) s.categories = categories;

  const projects: NonNullable<ContactQuery["projects"]> = {};
  const projInclude = ids(p, "proj");
  const projExclude = ids(p, "proj_x");
  if (projInclude.length) projects.include = projInclude;
  if (projExclude.length) projects.exclude = projExclude;
  if (Object.keys(projects).length) s.projects = projects;

  const zip = p.getAll("zip").map((z) => z.trim()).filter((z) => z && z.length <= 16);
  if (zip.length) s.zip = zip;
  const gender = oneOf(GENDERS, p.get("gender"));
  if (gender) s.gender = gender;
  const sort = oneOf(SORTS, p.get("sort"));
  if (sort) s.sort = sort;
  return s;
}

/**
 * The list state lives in the URL. Any patch without `keepPage` sends the reader back to page 1.
 * `frozenSearch`: the list shown behind an open contact. Its state comes from that search (the list's own URL
 * before the contact opened) and nothing it does changes the address, so it can never close the contact.
 */
export function useListState(frozenSearch?: string): [ListState, (patch: Partial<ListState>, opts?: { keepPage?: boolean; replace?: boolean }) => void] {
  const [params, setParams] = useSearchParams();
  const frozen = frozenSearch !== undefined;
  const state = useMemo(() => fromSearchParams(frozen ? new URLSearchParams(frozenSearch) : params), [frozen, frozenSearch, params]);
  const update = useCallback(
    (patch: Partial<ListState>, opts?: { keepPage?: boolean; replace?: boolean }) => {
      if (frozen) return;
      setParams((prev) => {
        const next = { ...fromSearchParams(prev), ...patch };
        if (!opts?.keepPage) next.page = 1;
        const out = new URLSearchParams();
        prev.forEach((v, k) => !OWNED.includes(k) && out.append(k, v));
        toSearchParams(next).forEach((v, k) => out.append(k, v));
        return out;
      }, { replace: opts?.replace });
    },
    [frozen, setParams],
  );
  return [state, update];
}
